import { useEffect, useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { opportunityService } from "@/services";
import { Loader2, TrendingUp, DollarSign, Trophy, Info } from "lucide-react";
import { formatCurrency } from "@/lib/currency";

const fmt = (v) => formatCurrency(v);

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={`forecast-kpi-${label}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-foreground mt-3">{value}</p>
    </div>
  );
}

export default function ForecastPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { opportunityService.forecast().then(setData).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const t = data?.totals || {};
  const rows = data?.byPeriod || [];

  const columns = [
    { key: "period", header: "Period", render: (r) => <span className="font-medium text-foreground">{r.period}</span> },
    { key: "count", header: "Open Deals", render: (r) => r.count },
    { key: "pipeline", header: "Pipeline", render: (r) => fmt(r.pipeline) },
    { key: "weighted", header: "Weighted Forecast", render: (r) => <span className="font-semibold text-primary">{fmt(r.weighted)}</span> },
  ];

  return (
    <div data-testid="forecast-page">
      <PageHeader title="Sales Forecast" subtitle="Weighted pipeline projection by expected close period" />

      <div className="bg-sky-500/10 border border-sky-500/30 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="forecast-placeholder-banner">
        <Info className="h-5 w-5 text-sky-500 shrink-0 mt-0.5" />
        <p className="text-sm text-foreground">This is a <strong>placeholder forecast</strong> computed as pipeline amount × stage win-probability. Advanced AI-driven forecasting (quotas, attainment, trend lines) arrives in a later phase.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
        <Kpi label="Open Opps" value={t.openOpportunities ?? 0} icon={TrendingUp} color="text-sky-500" />
        <Kpi label="Weighted Pipeline" value={fmt(t.weightedPipeline)} icon={DollarSign} color="text-primary" />
        <Kpi label="Won Deals" value={t.wonCount ?? 0} icon={Trophy} color="text-emerald-500" />
        <Kpi label="Won Amount" value={fmt(t.wonAmount)} icon={DollarSign} color="text-emerald-500" />
      </div>

      <DataTable columns={columns} rows={rows} loading={false} testid="forecast-table" empty="No open opportunities to forecast" />
    </div>
  );
}
